"use client";

import { ChevronDownIcon, SearchIcon } from "lucide-react";
import { useState } from "react";
import { HexAlphaColorPicker } from "react-colorful";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

// Comprehensive Tailwind color palette
const tailwindColors = [
	// Grays
	{ name: "Gray (50)", value: "#f9fafb" },
	{ name: "Gray (100)", value: "#f3f4f6" },
	{ name: "Gray (200)", value: "#e5e7eb" },
	{ name: "Gray (300)", value: "#d1d5db" },
	{ name: "Gray (400)", value: "#9ca3af" },
	{ name: "Gray (500)", value: "#6b7280" },
	{ name: "Gray (600)", value: "#4b5563" },
	{ name: "Gray (700)", value: "#374151" },
	{ name: "Gray (800)", value: "#1f2937" },
	{ name: "Gray (900)", value: "#111827" },
	{ name: "Gray (950)", value: "#030712" },

	// Slate
	{ name: "Slate (50)", value: "#f8fafc" },
	{ name: "Slate (100)", value: "#f1f5f9" },
	{ name: "Slate (200)", value: "#e2e8f0" },
	{ name: "Slate (300)", value: "#cbd5e1" },
	{ name: "Slate (400)", value: "#94a3b8" },
	{ name: "Slate (500)", value: "#64748b" },
	{ name: "Slate (600)", value: "#475569" },
	{ name: "Slate (700)", value: "#334155" },
	{ name: "Slate (800)", value: "#1e293b" },
	{ name: "Slate (900)", value: "#0f172a" },
	{ name: "Slate (950)", value: "#020617" },

	// Red
	{ name: "Red (50)", value: "#fef2f2" },
	{ name: "Red (100)", value: "#fee2e2" },
	{ name: "Red (200)", value: "#fecaca" },
	{ name: "Red (300)", value: "#fca5a5" },
	{ name: "Red (400)", value: "#f87171" },
	{ name: "Red (500)", value: "#ef4444" },
	{ name: "Red (600)", value: "#dc2626" },
	{ name: "Red (700)", value: "#b91c1c" },
	{ name: "Red (800)", value: "#991b1b" },
	{ name: "Red (900)", value: "#7f1d1d" },
	{ name: "Red (950)", value: "#450a0a" },

	// Orange
	{ name: "Orange (50)", value: "#fff7ed" },
	{ name: "Orange (100)", value: "#ffedd5" },
	{ name: "Orange (200)", value: "#fed7aa" },
	{ name: "Orange (300)", value: "#fdba74" },
	{ name: "Orange (400)", value: "#fb923c" },
	{ name: "Orange (500)", value: "#f97316" },
	{ name: "Orange (600)", value: "#ea580c" },
	{ name: "Orange (700)", value: "#c2410c" },
	{ name: "Orange (800)", value: "#9a3412" },
	{ name: "Orange (900)", value: "#7c2d12" },
	{ name: "Orange (950)", value: "#431407" },

	// Yellow
	{ name: "Yellow (50)", value: "#fefce8" },
	{ name: "Yellow (100)", value: "#fef3c7" },
	{ name: "Yellow (200)", value: "#fde68a" },
	{ name: "Yellow (300)", value: "#fcd34d" },
	{ name: "Yellow (400)", value: "#fbbf24" },
	{ name: "Yellow (500)", value: "#f59e0b" },
	{ name: "Yellow (600)", value: "#d97706" },
	{ name: "Yellow (700)", value: "#b45309" },
	{ name: "Yellow (800)", value: "#92400e" },
	{ name: "Yellow (900)", value: "#78350f" },
	{ name: "Yellow (950)", value: "#451a03" },

	// Green
	{ name: "Green (50)", value: "#f0fdf4" },
	{ name: "Green (100)", value: "#dcfce7" },
	{ name: "Green (200)", value: "#bbf7d0" },
	{ name: "Green (300)", value: "#86efac" },
	{ name: "Green (400)", value: "#4ade80" },
	{ name: "Green (500)", value: "#22c55e" },
	{ name: "Green (600)", value: "#16a34a" },
	{ name: "Green (700)", value: "#15803d" },
	{ name: "Green (800)", value: "#166534" },
	{ name: "Green (900)", value: "#14532d" },
	{ name: "Green (950)", value: "#052e16" },

	// Blue
	{ name: "Blue (50)", value: "#eff6ff" },
	{ name: "Blue (100)", value: "#dbeafe" },
	{ name: "Blue (200)", value: "#bfdbfe" },
	{ name: "Blue (300)", value: "#93c5fd" },
	{ name: "Blue (400)", value: "#60a5fa" },
	{ name: "Blue (500)", value: "#3b82f6" },
	{ name: "Blue (600)", value: "#2563eb" },
	{ name: "Blue (700)", value: "#1d4ed8" },
	{ name: "Blue (800)", value: "#1e40af" },
	{ name: "Blue (900)", value: "#1e3a8a" },
	{ name: "Blue (950)", value: "#172554" },

	// Indigo
	{ name: "Indigo (50)", value: "#eef2ff" },
	{ name: "Indigo (100)", value: "#e0e7ff" },
	{ name: "Indigo (200)", value: "#c7d2fe" },
	{ name: "Indigo (300)", value: "#a5b4fc" },
	{ name: "Indigo (400)", value: "#818cf8" },
	{ name: "Indigo (500)", value: "#6366f1" },
	{ name: "Indigo (600)", value: "#4f46e5" },
	{ name: "Indigo (700)", value: "#4338ca" },
	{ name: "Indigo (800)", value: "#3730a3" },
	{ name: "Indigo (900)", value: "#312e81" },
	{ name: "Indigo (950)", value: "#1e1b4b" },

	// Purple
	{ name: "Purple (50)", value: "#faf5ff" },
	{ name: "Purple (100)", value: "#f3e8ff" },
	{ name: "Purple (200)", value: "#e9d5ff" },
	{ name: "Purple (300)", value: "#d8b4fe" },
	{ name: "Purple (400)", value: "#c084fc" },
	{ name: "Purple (500)", value: "#a855f7" },
	{ name: "Purple (600)", value: "#9333ea" },
	{ name: "Purple (700)", value: "#7c3aed" },
	{ name: "Purple (800)", value: "#6b21a8" },
	{ name: "Purple (900)", value: "#581c87" },
	{ name: "Purple (950)", value: "#3b0764" },

	// Pink
	{ name: "Pink (50)", value: "#fdf2f8" },
	{ name: "Pink (100)", value: "#fce7f3" },
	{ name: "Pink (200)", value: "#fbcfe8" },
	{ name: "Pink (300)", value: "#f9a8d4" },
	{ name: "Pink (400)", value: "#f472b6" },
	{ name: "Pink (500)", value: "#ec4899" },
	{ name: "Pink (600)", value: "#db2777" },
	{ name: "Pink (700)", value: "#be185d" },
	{ name: "Pink (800)", value: "#9d174d" },
	{ name: "Pink (900)", value: "#831843" },
	{ name: "Pink (950)", value: "#500724" },
];

export interface TailwindColorPickerProps {
	defaultValue?: string | null;
	onValueChange?: (value: string) => void;
	className?: string;
}

export const TailwindColorPicker = ({ defaultValue, onValueChange, className }: TailwindColorPickerProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState("");
	const [activeTab, setActiveTab] = useState("tailwind");
	const [currentValue, setCurrentValue] = useState(defaultValue || "");

	// Check if current value is a Tailwind color
	const currentTailwindColor = tailwindColors.find(
		(color) => color.value === currentValue || color.name === currentValue,
	);

	// Filter colors based on search
	const filteredColors = tailwindColors.filter((color) =>
		color.name.toLowerCase().includes(search.toLowerCase()),
	);

	const handleColorSelect = (colorValue: string) => {
		setCurrentValue(colorValue);
		onValueChange?.(colorValue);
		setIsOpen(false);
	};

	const handleCustomColorChange = (color: string) => {
		setCurrentValue(color.toUpperCase());
		onValueChange?.(color);
	};

	// Get display value for the trigger
	const getDisplayValue = () => {
		if (!currentValue) return "Default";
		if (currentTailwindColor) return currentTailwindColor.name;
		return currentValue;
	};

	// Get preview color
	const getPreviewColor = () => {
		if (!currentValue) return "#6b7280"; // Default gray-500
		if (currentTailwindColor) return currentTailwindColor.value;
		return currentValue;
	};

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen} modal={true}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className={cn(
						"w-full justify-between rounded-sm shadow-none text-neutral-600",
						!currentValue && "text-muted-foreground",
						className,
					)}
				>
					<div className="flex items-center gap-2">
						<div
							className="size-4 border rounded-xs border-neutral-200"
							style={{ backgroundColor: getPreviewColor() }}
						/>
						{getDisplayValue()}
					</div>
					<ChevronDownIcon className="h-4 w-4 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-64 p-1" align="start" data-yns-click>
				<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
					<TabsList className="grid w-full grid-cols-2">
						<TabsTrigger className="text-xs" value="tailwind">
							Tailwind
						</TabsTrigger>
						<TabsTrigger className="text-xs" value="custom">
							Custom
						</TabsTrigger>
					</TabsList>

					<TabsContent value="tailwind" className="space-y-2">
						<div className="relative">
							<SearchIcon className="absolute left-2 top-3 size-4 text-muted-foreground" />
							<Input
								placeholder="Search for colors..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="pl-8"
							/>
						</div>

						{/* Default option */}
						<div className="space-y-2">
							<Button
								variant="ghost"
								className="w-full justify-start p-2 h-auto"
								onClick={() => handleColorSelect("")}
							>
								<div className="flex items-center gap-2">
									<div className="size-4 rounded-xs border border-neutral-200 bg-gray-500" />
									<span>Default</span>
								</div>
							</Button>
						</div>

						{/* Color grid */}
						<div className="max-h-64 overflow-y-auto">
							<div className="grid grid-cols-1">
								{filteredColors.map((color) => (
									<Button
										key={color.name}
										variant="ghost"
										className="w-full justify-start p-2 h-auto hover:bg-neutral-50"
										onClick={() => handleColorSelect(color.value)}
									>
										<div className="flex items-center gap-2">
											<div
												className="size-4 rounded-xs border border-neutral-200 bg-gray-500"
												style={{ backgroundColor: color.value }}
											/>
											<span className="text-sm">{color.name}</span>
										</div>
									</Button>
								))}
							</div>
						</div>
					</TabsContent>

					<TabsContent value="custom">
						<HexAlphaColorPicker color={currentValue || "#6b7280ff"} onChange={handleCustomColorChange} />
					</TabsContent>
				</Tabs>
			</PopoverContent>
		</Popover>
	);
};
